import {EChannelType, EContentType, TItemId} from 'src/constants';
import {
  THeader,
  TMessageMatcher,
  TMessageMatcherButton,
  TTHeaderType,
} from 'src/containers/MessageMatchers/definitions';
import type {TButton} from 'src/containers/Report/definitions';
import {
  EAddress,
  type TAddressItem,
  type TSubscriber,
} from 'src/containers/Subscribers/definitions';
import PATHS from 'src/paths';
import {createEventEmitter} from 'src/utils';

import {EChannelParameter, ESystemParameter} from '../Parameters/definitions';
import {TDialogSubscriber, TMessageContent} from './definitions';

const mapAttachment = (attachment: NonNullable<TMessageContent['attachment']>): TFile => ({
  fileUrl: attachment.url ?? '',
  id: 0,
  originalFileName: attachment.name ?? '',
  size: attachment.size ?? 0,
});

const mapHeader = (source: NonNullable<TMessageContent['header']>): THeader => {
  const header: THeader = {};

  if (source.text) {
    header.text = source.text;
    header.headerType = EContentType.TEXT as TTHeaderType;
  }

  if (source.imageUrl) {
    header.headerType = EContentType.IMAGE as TTHeaderType;
    header.headerExampleMediaUrl = source.imageUrl;
  } else if (source.videoUrl) {
    header.headerType = EContentType.VIDEO as TTHeaderType;
    header.headerExampleMediaUrl = source.videoUrl;
  } else if (source.documentUrl) {
    header.headerType = EContentType.DOCUMENT as TTHeaderType;
    header.headerExampleMediaUrl = source.documentUrl;
  }

  return header;
};

const mapButton = (button: TButton): TMessageMatcherButton => ({
  buttonType: button.type,
  text: button.text,
  payload: button.payload,
  phone: button.phone,
  url: button.url,
  shouldCollectClicks: button.shouldCollectClicks,
});

export const mapToDialogContent = (source: TMessageContent): TMessageMatcher['content'] => {
  const result: TMessageMatcher['content'] = {};

  if (source.text) {
    result.text = source.text;
  }

  if (source.attachment) {
    result.attachment = mapAttachment(source.attachment);
  }

  if (source.header) {
    result.header = mapHeader(source.header);
  }

  if (source.footer?.text) {
    result.footer = {text: source.footer.text};
  }

  if (source.buttons?.length) {
    result.buttons = source.buttons.map(mapButton);
  }

  if (source.codeExpirationMinutes !== null) {
    result.codeExpirationMinutes = source.codeExpirationMinutes;
  }

  if (source.securityRecommendation !== null) {
    result.securityRecommendation = source.securityRecommendation;
  }

  if (source.requestCallPermission !== null) {
    result.requestCallPermission = source.requestCallPermission;
  }

  const {longitude, latitude, address} = source.location ?? {};

  if (longitude !== null || latitude !== null || address) {
    result.location = {longitude, latitude, address};
  }

  return result;
};

export const CHANNEL_ADDRESS_TYPES: Record<string, EAddress[]> = {
  [EChannelType.WHATSAPP]: [EAddress.PHONE, EAddress.WHATSAPP_BSUID],
  [EChannelType.MAX]: [EAddress.PHONE],
};

export const canInitiateChannel = (channelType: string, addresses?: TAddressItem[]): boolean =>
  CHANNEL_ADDRESS_TYPES[channelType]?.some((type) =>
    addresses?.some((a) => a.type === type && a.address),
  ) ?? false;

export const DIALOG_ADDRESS_TYPES: EAddress[] = [
  EAddress.PHONE,
  EAddress.WHATSAPP_BSUID,
  EAddress.MAX_ID,
  EAddress.TELEGRAM_ID,
];

export const getSubscriberDialogAddress = (subscriber?: TSubscriber): TAddressItem | undefined => {
  const addresses = subscriber?.addresses;

  if (!addresses) {
    return undefined;
  }

  for (const type of DIALOG_ADDRESS_TYPES) {
    const found = addresses.find((a) => a.type === type && a.address);

    if (found) {
      return found;
    }
  }

  return undefined;
};

const AVATAR_EMOJIS = ['🦊', '🦁', '🐻', '🐨', '🦉', '🐬', '🦅', '🐺'];

export const getAvatarEmoji = (name?: string): string => {
  if (!name) {
    return AVATAR_EMOJIS[0];
  }

  const normalizedName = name.trim().toLowerCase();

  let hash = 0x811c9dc5;

  for (const char of normalizedName) {
    hash ^= char.codePointAt(0) || 0;
    hash = Math.imul(hash, 0x01000193);
  }

  const positiveHash = hash >>> 0;

  return AVATAR_EMOJIS[positiveHash % AVATAR_EMOJIS.length];
};

export enum EEventType {
  DIALOG_MESSAGE_SENT = 'DIALOG_MESSAGE_SENT',
}

export const emitter = createEventEmitter<EEventType>();

export const buildNewDialogUrl = (params: {
  subscriberAddress: string;
  subscriberAddressType: string;
  channelType: string;
  subjectId: TItemId;
}): string => {
  const searchParams = new URLSearchParams({
    subscriberAddress: params.subscriberAddress,
    subscriberAddressType: params.subscriberAddressType,
    channelType: params.channelType,
    subjectId: String(params.subjectId),
  });

  return `${PATHS.DIALOGS_NEW}?${searchParams}`;
};

export const getSubscriberName = (
  addressesByType: TDialogSubscriber['addresses'],
  parametersByCode: TDialogSubscriber['parameters'],
) => {
  const nameFromParameters = [
    [parametersByCode[ESystemParameter.FIRST_NAME], parametersByCode[ESystemParameter.LAST_NAME]],
    parametersByCode[EChannelParameter.WHATSAPP_DISPLAY_NAME],
    parametersByCode[EChannelParameter.WHATSAPP_USERNAME],
    [
      parametersByCode[EChannelParameter.MAX_FIRST_NAME],
      parametersByCode[EChannelParameter.MAX_LAST_NAME],
    ],
  ]
    .map((parameter) => {
      if (Array.isArray(parameter)) {
        return parameter
          .map((item) => item?.value)
          .filter((item) => Boolean(item))
          .join(' ');
      }

      return String(parameter?.value ?? '');
    })
    .filter((name) => Boolean(name))[0];

  if (nameFromParameters) {
    return nameFromParameters;
  }

  const nameFromAddresses = DIALOG_ADDRESS_TYPES.map((address) => addressesByType[address])
    .map((address) => address?.address)
    .filter((name) => Boolean(name))[0];

  return nameFromAddresses ?? '';
};
